import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';
import { Slot } from 'radix-ui';
import * as React from 'react';

/**
 * Figma: "Badge". A small status or category label (what Ant Design called a
 * `Tag`). The feedback tones pair the `-muted` background with the `-text`
 * colour of each state, so a badge never has to improvise a contrast.
 */
const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary: 'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive: 'border-transparent bg-destructive text-destructive-foreground [a&]:hover:bg-destructive/90',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        info: 'border-transparent bg-info-muted text-info-text',
        success: 'border-transparent bg-success-muted text-success-text',
        warning: 'border-transparent bg-warning-muted text-warning-text',
        'destructive-muted': 'border-transparent bg-destructive-muted text-destructive-text',
        inverse: 'border-transparent bg-inverse text-inverse-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
